import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StoreIcon from '@mui/icons-material/Store';
import AppHeader from '../components/AppHeader';
import { useBranch } from '../contexts/BranchContext';
import { getDashboardMetrics } from '../services/dashboardService';

const StatCard = ({ title, value, icon, color, onPress, isAmount = false }) => (
  <Card
    onClick={onPress}
    sx={{
      cursor: onPress ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onPress ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
      border: onPress ? '1px solid #E2E8F0' : 'none',
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#64748B', flex: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 700,
          color: '#1E293B',
        }}
      >
        {isAmount ? `₹${value.toLocaleString()}` : value}
      </Typography>
      {onPress && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <ArrowForwardIcon sx={{ fontSize: 16, color: '#851E41' }} />
        </Box>
      )}
    </CardContent>
  </Card>
);

const MetricItem = ({ label, value, isWarning }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.5,
      borderBottom: '1px solid #F1F5F9',
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#64748B' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 600,
        color: isWarning ? '#EF4444' : '#1E293B',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { selectedBranch, restaurants, allBranches, listenAllMode, requestBranchSelect } = useBranch();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (listenAllMode && (restaurants?.length > 1 || allBranches?.length > 1)) {
      requestBranchSelect();
    }
  }, [listenAllMode, restaurants?.length, allBranches?.length, requestBranchSelect]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedBranch?._id) return;

    try {
      const data = await getDashboardMetrics(selectedBranch._id);
      setMetrics(data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      fetchMetrics();
    }
  }, [selectedBranch, fetchMetrics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMetrics();
    setRefreshing(false);
  };

  const handleTotalOrdersPress = () => {
    navigate('/total-orders');
  };

  if (listenAllMode && (restaurants?.length > 1 || allBranches?.length > 1) && !selectedBranch) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, p: 3 }}>
          <StoreIcon sx={{ fontSize: 64, color: '#94A3B8', mb: 2 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1E293B', mb: 1, textAlign: 'center' }}>
            Select a Branch
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3, textAlign: 'center', maxWidth: 300 }}>
            You're in listen-all mode. Please select a specific branch to view its dashboard metrics.
          </Typography>
          <Button
            variant="contained"
            onClick={requestBranchSelect}
            sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' } }}
          >
            Choose Branch
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress sx={{ color: '#851E41' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader />
      
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
            Welcome back!
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#64748B' }}>
            {selectedBranch?.name || 'Restaurant Dashboard'}
          </Typography>
        </Box>

        <Card
          sx={{
            bgcolor: '#851E41',
            borderRadius: 4,
            mb: 3,
            boxShadow: '0 4px 12px rgba(133, 30, 65, 0.3)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <TrendingUpIcon sx={{ color: '#fff', mr: 1 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
                Total Revenue
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#fff', mb: 0.5 }}>
              ₹{metrics?.totalRevenue?.toLocaleString() || '0'}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
              All time earnings
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <StatCard
              title="Total Orders"
              value={metrics?.totalOrders || 0}
              icon={<ReceiptIcon sx={{ fontSize: 20, color: '#851E41' }} />}
              color="#851E41"
              onPress={handleTotalOrdersPress}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Pending Orders"
              value={metrics?.pendingOrders || 0}
              icon={<HourglassEmptyIcon sx={{ fontSize: 20, color: '#F59E0B' }} />}
              color="#F59E0B"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Completed Orders"
              value={metrics?.completedOrders || 0}
              icon={<CheckCircleIcon sx={{ fontSize: 20, color: '#10B981' }} />}
              color="#10B981"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Cancelled Orders"
              value={metrics?.cancelledOrders || 0}
              icon={<CancelIcon sx={{ fontSize: 20, color: '#EF4444' }} />}
              color="#EF4444"
            />
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1E293B', mb: 2 }}>
              Performance Metrics
            </Typography>
            <MetricItem
              label="Average Order Value"
              value={`₹${metrics?.averageOrderValue?.toFixed(2) || '0'}`}
            />
            <MetricItem
              label="Cancellation Rate"
              value={`${metrics?.cancellationRate || 0}%`}
              isWarning={metrics?.cancellationRate > 5}
            />
            <MetricItem
              label="Avg Prep Time"
              value={`${metrics?.avgPrepTime || 0} min`}
            />
          </CardContent>
        </Card>
      </Box>

      {refreshing && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#1E293B',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 2,
            zIndex: 1000,
          }}
        >
          <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
          Refreshing...
        </Box>
      )}
    </Box>
  );
};

export default DashboardPage;